import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../../../../store/utility';
import { backend_server_address } from '../../../../../backendServer';
import type { AssetAuthor } from '../../summary/types';
import type { Diagnostic } from '../Diagnostic/types';
import { plainTextItems } from '../../../../utilities/richText';
import { refreshNodeHealth } from '../../../../../store/actions/treeActions/refreshNodeHealth';

/** Last diagnostic embedded in an active incident: the shared `Diagnostic` shape plus the
 *  `author`/`best_practices` fields the feed cards + asset drawer read. */
export interface ActiveIncidentDiagnostic extends Diagnostic {
    author?: AssetAuthor | null;
    best_practices?: string[];
}

export interface ActiveIncident {
    node: number;
    start_date?: string | null;
    last_diagnostic?: ActiveIncidentDiagnostic;
}

/** `GET /api/incident/active/` → active incidents keyed by node id. */
export type ActiveIncidentsResponse = Record<number, ActiveIncident>;

/** Object a close/review call reports as created. Only `id` is consumed — staged attachments go to it. */
export interface CloseIncidentObject {
    id?: number;
}

/** `POST /api/incident/close/` → the closed incident plus the objects it created. On a `must-create`
 *  close both are new (the intervention and its implicit win); on `can-link`, `intervention` is the
 *  already-linked one and `improvement` is the new win. */
export interface CloseIncidentResponse {
    id?: number;
    node?: number;
    result?: string;
    intervention?: CloseIncidentObject | null;
    improvement?: CloseIncidentObject | null;
}

/** `POST /api/review/` → the created review, plus the win when the review is a partial win. Mirrors
 *  `incident/close/`: the win is created as part of the call, so `improvement.id` is the only place its id
 *  appears, and it is what DiagnosticForm.handlePartialWinConfirm uploads the staged attachments to.
 *  Optional because a plain review (not a partial win) creates no win. */
export interface CreateReviewResponse {
    id?: number;
    improvement?: CloseIncidentObject | null;
}

/** Review payload. Loosely typed on purpose: the diagnosis/review path builds it from the whole form
 *  (findings, evidences, problems...), and only `node` is read here for cache invalidation. */
export interface CreateReviewArg {
    node: number;
    improvement?: Record<string, unknown>;
    [key: string]: unknown;
}

/** Close payload. `intervention`/`improvement` are the nested objects the backend creates as part of
 *  closing — they must stay in the payload (see DiagnosticForm.handleCloseIncidentConfirm). */
export interface CloseIncidentArg {
    node: number;
    result: string;
    reason?: string;
    intervention?: Record<string, unknown>;
    improvement?: Record<string, unknown>;
    comments?: string;
    markdown?: string;
}

/** A review or an incident close re-evaluates the asset's condition in the same request: a review
 *  floors it at attention, a close lets it and its points fall back to their real condition. */
const refreshHealthOnSuccess = async (
    arg: { node: number },
    { dispatch, queryFulfilled }: { dispatch: (action: any) => unknown; queryFulfilled: Promise<unknown> },
) => {
    try {
        await queryFulfilled;
        dispatch(refreshNodeHealth(arg.node));
    } catch {
        // The failure surfaces via the mutation's own state; there is no new condition to read.
    }
};

export const reviewAPI = createApi({
    reducerPath: 'reviewAPI',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address + '/api/' }),
    tagTypes: ['ActiveIncident'],
    endpoints: (builder) => ({
        createReview: builder.mutation<CreateReviewResponse, CreateReviewArg>({
            query: (data) => ({
                url: 'review/',
                method: 'POST',
                withCredentials: true,
                data,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'ActiveIncident' as const, id: `LIST:${arg.node}` },
                { type: 'ActiveIncident' as const, id: arg.node },
            ],
            onQueryStarted: refreshHealthOnSuccess,
        }),
        getActiveIncidents: builder.query<ActiveIncidentsResponse, { node: number | null }>({
            query: (params) => ({
                url: 'incident/active/',
                method: 'GET',
                withCredentials: true,
                params,
            }),
            // `best_practices` can arrive as the diagnostic's rich-text comment, which is not a
            // best practice at all (see richText.ts). Drop it once here for every consumer.
            transformResponse: (response: ActiveIncidentsResponse) =>
                Object.fromEntries(
                    Object.entries(response ?? {}).map(([nodeId, incident]) => {
                        const practices = incident?.last_diagnostic?.best_practices;
                        if (!Array.isArray(practices)) return [nodeId, incident];
                        return [
                            nodeId,
                            {
                                ...incident,
                                last_diagnostic: {
                                    ...incident.last_diagnostic,
                                    best_practices: plainTextItems(practices),
                                },
                            },
                        ];
                    }),
                ),
            providesTags: (result, error, arg) => {
                const listTag = { type: 'ActiveIncident' as const, id: `LIST:${arg.node}` };
                if (!result) return [listTag];
                const incidentTags = Object.values(result).map((incident) => ({
                    type: 'ActiveIncident' as const,
                    id: incident.node,
                }));

                return [listTag, ...incidentTags];
            },
        }),
        closeIncident: builder.mutation<CloseIncidentResponse, CloseIncidentArg>({
            query: (data) => ({
                url: 'incident/close/',
                method: 'POST',
                withCredentials: true,
                data,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'ActiveIncident' as const, id: `LIST:${arg.node}` },
                { type: 'ActiveIncident' as const, id: arg.node },
            ],
            onQueryStarted: refreshHealthOnSuccess,
        }),
    }),
});

export const { useCreateReviewMutation, useGetActiveIncidentsQuery, useCloseIncidentMutation } = reviewAPI;
export const reviewAPIReducer = reviewAPI.reducer;
export const reviewAPIReducerPath = reviewAPI.reducerPath;
export const reviewAPIReducerMiddleware = reviewAPI.middleware;
